import { equipmentContextControls } from "../../utils/equipmentContexts"
import { useCallback, useMemo } from "react"
import { ICloudRegulatoryControlPoint, ICloudRegulatoryControlTemplate } from "../../types"
import type {
  RegulatoryScope,
  EquipmentContextQuestionnaire,
  RegulatoryQuestionnaire,
  ResolvedCatalog,
} from "../../types/regulatory"
import { allCatalogPages, regulatoryApi, resetRegulatoryCache } from "../../utils/regulatoryApi"
import { presentControls, presentTemplate } from "../../utils/regulatoryPresentation"
import { useCatalogResource } from "./useCatalog"
export function useRegulatoryTemplates(scope?: RegulatoryScope) {
  const resource = useCatalogResource(
    useCallback(
      (signal) =>
        allCatalogPages((filters, s) => regulatoryApi.templates({ ...filters, scope }, s), signal),
      [scope],
    ),
  )
  const templates = useMemo(
    () => resource.data?.map((t) => presentTemplate(t)) ?? [],
    [resource.data],
  )
  return { ...resource, templates }
}
export async function loadAssignedCatalog(
  type: "site" | "equipment",
  uuid: string,
  signal: AbortSignal,
) {
  const contextual: EquipmentContextQuestionnaire | null =
    type === "equipment" ? await regulatoryApi.equipmentQuestionnaire(uuid, signal) : null
  if (contextual?.configuration.enabled) {
    const roots = Object.values(contextual.resolvedAssignments)
    const first = roots[0]
    const points = equipmentContextControls(contextual)
    const resolved: ResolvedCatalog | null = first
      ? {
          ...first,
          templates: roots.flatMap((r) => r.templates),
          controls: contextual.controls
            .filter((c) => c.displayContextUuid === c.answerContextUuid)
            .map((c) => c.sources[0]!.control),
          missingParts: Array.from(new Set(roots.flatMap((r) => r.missingParts))),
          coverageGaps: roots.reduce((n, r) => n + r.coverageGaps, 0),
          interpretationReserves: roots.reduce((n, r) => n + r.interpretationReserves, 0),
          allReviewed: roots.every((r) => r.allReviewed),
        }
      : null
    const assignments = contextual.configuration.contexts.flatMap((c) => c.assignments)
    const questionnaire: RegulatoryQuestionnaire = {
      targetType: "equipment",
      targetUuid: uuid,
      templateUuid: first?.templateUuid ?? null,
      templateRevisionUuid: first?.revisionUuid ?? null,
      versioned: assignments.length > 0 && assignments.every((a) => !!a.templateRevisionUuid),
      resolved,
    }
    return { contextual, questionnaire, resolved, detail: null, template: null, points }
  }
  const questionnaire = await regulatoryApi.questionnaire(type, uuid, signal)
  const resolved = questionnaire.resolved
  if (
    !questionnaire.versioned ||
    !resolved ||
    !questionnaire.templateUuid ||
    !questionnaire.templateRevisionUuid
  )
    return {
      contextual,
      questionnaire,
      resolved: null,
      detail: null,
      template: null,
      points: [] as ICloudRegulatoryControlPoint[],
    }
  const [current, revision] = await Promise.all([
    regulatoryApi.template(questionnaire.templateUuid, signal),
    regulatoryApi.revision(questionnaire.templateUuid, questionnaire.templateRevisionUuid, signal),
  ])
  const detail = { template: current.template, revision }
  return {
    contextual,
    questionnaire,
    resolved,
    detail,
    template: presentTemplate(detail.template, detail, resolved),
    points: contextual ? equipmentContextControls(contextual) : presentControls(resolved),
  }
}
export function useAssignedCatalog(type: "site" | "equipment", uuid: string, assignmentKey = "") {
  const resource = useCatalogResource(
    useCallback(
      async (signal) => (uuid ? loadAssignedCatalog(type, uuid, signal) : null),
      [type, uuid, assignmentKey],
    ),
  )
  return {
    ...resource,
    template: resource.data?.template ?? null,
    points: resource.data?.points ?? [],
  }
}
export type QuestionnaireTarget = { type: "site" | "equipment"; uuid: string; revision?: string }
export function useAssignedCatalogs(targets: QuestionnaireTarget[]) {
  const key = JSON.stringify(targets.filter((t) => t.uuid))
  const resource = useCatalogResource(
    useCallback(
      async (signal) => {
        const entries = await Promise.all(
          (JSON.parse(key) as QuestionnaireTarget[]).map(
            async (t) => [t.uuid, await loadAssignedCatalog(t.type, t.uuid, signal)] as const,
          ),
        )
        return Object.fromEntries(entries)
      },
      [key],
    ),
  )
  const templates = useMemo(
    () => Object.values(resource.data ?? {}).flatMap((c) => (c.template ? [c.template] : [])),
    [resource.data],
  )
  const pointsByTarget = useMemo(
    () =>
      Object.fromEntries(Object.entries(resource.data ?? {}).map(([uuid, c]) => [uuid, c.points])),
    [resource.data],
  )
  return { ...resource, templates, pointsByTarget }
}
export function refreshCatalog() {
  resetRegulatoryCache()
}
